/**
  Author: Suneel Singh - suneel.singh@enabledata.com
  Purpose: Organization users list component
  Date: Sept 2020
**/

import React from 'react';
import { makeStyles, Grid, Tooltip, IconButton } from '@material-ui/core';
import moment from 'moment';
import BlockIcon from '@material-ui/icons/Block';
import EditIcon from '@material-ui/icons/Edit';
import DescriptionIcon from '@material-ui/icons/Description';
import AddBoxIcon from '@material-ui/icons/AddBox';
import { useStoreState, useStoreActions } from 'easy-peasy';

import {
  globalDateEntity,
  globalPageTypeEntity,
  currentUserEntity
} from '../../constants/applicationConstants';
import { stringConstants } from '../../constants/stringConstants';
import EnhancedTable from '../enhancedTable/enhancedTable';
import DialogActiveInactiveEntityContainer from '../dialogActiveInactiveEntity/dialogActiveInactiveEntity';
import DialogOrgsUser from '../dialogOrgsUser/dialogOrgsUser';

/** STYLES HERE **/
const useStyles = makeStyles(() => ({
  bWhite: {
    backgroundColor: '#fff',
  },
}));
/** ASSOCIATION LIST COMPONENT * */
const OrgsUsers = (props) => {
  const classes = useStyles();
  const {
    addedOrgsId,
    pageType
  } = props;

  const [skipPageReset] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [openUserForm, setOpenUserForm] = React.useState(false);
  const [type, setType] = React.useState('');
  const [dailogHeading, setDailogHeading] = React.useState('');
  const [selectedRow, setSelectedRow] = React.useState(null); //Set selected rows
  const [userRole, setUserRole] = React.useState([]);
  const [updateUserFormData, setUpdateUserFormData] = React.useState({});

  const commonAction = useStoreActions((actions) => actions.common);
  const roleState = useStoreState((state) => state.roles.roleList);
  const roleAction = useStoreActions((actions) => actions.roles);
  const userAction = useStoreActions((actions) => actions.userList);
  const userState = useStoreState((state) => state.userList);
  const orgState = useStoreState((state) => state.organization);

  // Columns for Table
  const columns = [
    {
      Header: 'User Name',
      sortType: 'caseInsensitive',
      accessor: 'username'
    },
    {
      Header: 'First Name',
      sortType: 'caseInsensitive',
      accessor: 'firstname'
    },
    {
      Header: 'Last Name',
      sortType: 'caseInsensitive',
      accessor: 'lastname',
    },
    {
      Header: 'Email Address',
      accessor: 'email'
    },
    {
      Header: 'Status',
      Cell: ({ row }) => {
        return (
          <span style={{ textTransform: 'capitalize' }}>
            {row.original.current_status
              ? row.original.current_status.toLowerCase()
              : ''}
          </span>
        );
      },
    },
    {
      Header: ' ',
      Cell: ({ row }) => (
        <Grid>
          <div style={{ display: 'flex' }}>
            { (typeof row.original?.is_sso_user === 'undefined') ? (
              <Tooltip title="Edit User">
                <IconButton
                  color="primary"
                  disabled={pageType === globalPageTypeEntity.view}
                  aria-label="Edit User"
                  onClick={() => userAddEditHandler(
                    event,
                    globalPageTypeEntity.edit,
                    row.original
                  )}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
            ) :  (<Tooltip title="View User">
            <IconButton
              color="primary"
              disabled={pageType === globalPageTypeEntity.view}
              aria-label="View User"
              onClick={() => userAddEditHandler(
                event,
                globalPageTypeEntity.view,
                row.original
              )}
            >
              <DescriptionIcon />
            </IconButton>
          </Tooltip>)
          }

            
            <Tooltip title="Disable User">
              <IconButton
                color="primary"
                disabled={row.original.current_status === 'INACTIVE' || pageType === globalPageTypeEntity.view || ( (typeof row.original?.is_sso_user !== 'undefined') && row.original?.is_sso_user==='y') || (row.original?.root_user && row.original?.root_user==1)}
                aria-label="Disable User"
                onClick={() => deactivateHandler(
                  event,
                  row.original,
                  globalPageTypeEntity.deactivate,
                )}
              >
                <BlockIcon />
              </IconButton>
            </Tooltip>
          </div>
        </Grid>
      ),
    },
  ];

  const deactivateHandler = (e, row, op = '') => {
    setOpen(true);
    setType(op);
    setDailogHeading('Disable User');
    setSelectedRow(row);
  };

  const dialogClose = (responseParam) => {
    setOpen(false);
    if (responseParam) {
      let momentDate = moment(responseParam.inactiveDate).format(globalDateEntity.dateTimeFormatPost);
      const deactivateUserPayload = selectedRow;
      deactivateUserPayload.current_status = 'INACTIVE';
      deactivateUserPayload.reason = responseParam.reason;
      deactivateUserPayload.inactivedate = momentDate;
      deactivateUserPayload.role = selectedRow.roles;
      userAction.updateUser(deactivateUserPayload)
        .then(function (response) {
          userAction.getUserList(addedOrgsId);
        })
        .catch(function (error) {
          console.log('Error', error);
          commonAction.setSubmitSnackbar({
            status: true,
            messages: stringConstants.recordInactivationFailedMsg,
            error: true,
          });
        });
    }
  };

  const userAddEditHandler = (e, op = '', row = '') => {

    if (op == globalPageTypeEntity.edit) {
      let emailDiassemble = row.email.split('@');
      row.email_username = emailDiassemble[0] ? emailDiassemble[0] : '';
      row.email_domain = emailDiassemble[1] ? emailDiassemble[1] : '';
    }

    if (row.email_domain=='' && orgState.orgsData && orgState.orgsData.config && orgState.orgsData.config.allowed_domains) {
      row.email_domain = orgState.orgsData.config.allowed_domains[0].domain;
    }


    setUpdateUserFormData(row);
    setOpenUserForm(true);
    setType(op);
    setDailogHeading(`${op} User`);
  };

  React.useEffect(() => {
    roleAction.getRoleList();
  }, []);

  React.useEffect(() => {
    if (addedOrgsId) {
      userAction.getUserList(addedOrgsId);
    }
  }, [addedOrgsId]);

  React.useEffect(() => {
    const userRoleAdd =
      roleState &&
      roleState.length > 0 &&
      roleState.filter(function (item) {
        return item.title.toLowerCase() === currentUserEntity.tenantAdminTitle;
      });
    setUserRole(userRoleAdd);
  }, [roleState]);

  return (
    <>
      <Grid item md={12} className={`${classes.bWhite}`}>
        <Grid item md={12} className={`${classes.authBox}`}>
          <EnhancedTable
            columns={columns}
            data={ userState.userList }
            rowSelection={
              false //pageType === globalPageTypeEntity.view ? false : true
            }
            skipPageReset={skipPageReset}
            toolbar={{
              show: true,
              title: 'Users',
              enableSearch: true,
              actionButtonGroup: (selected) => {
                const noOfSelection = Object.keys(selected).length;
                const selecterRows = Object.keys(selected) &&
                  Object.keys(selected).length > 0 &&
                  Object.keys(selected).map((selectedKey) => {
                    return userState.userList[selectedKey];
                  });
                return (
                  <div style={{ display: 'flex' }}>
                    <Tooltip title="Add User">
                      <IconButton
                        color="primary"
                        disabled={pageType === globalPageTypeEntity.view || !addedOrgsId}
                        aria-label="Add"
                        onClick={() => userAddEditHandler(
                          event,
                          globalPageTypeEntity.add,
                        )}
                      >
                        <AddBoxIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip style={{ display: 'none' }} title="Disable User">
                      <IconButton
                        color="primary"
                        aria-label="deactivate"
                        onClick={() => deactivateHandler(
                          event,
                          selecterRows,
                          globalPageTypeEntity.deactivate,
                        )}
                        disabled={pageType === globalPageTypeEntity.view || noOfSelection === 0}
                      >

                      </IconButton>
                    </Tooltip>
                    <Tooltip style={{ display: 'none' }} title="Delete User">
                      <IconButton
                        color="primary"
                        disabled={pageType === globalPageTypeEntity.view || noOfSelection === 0}
                        aria-label="Delete"
                      >

                      </IconButton>
                    </Tooltip>
                  </div>
                );
              },
            }}
          />
        </Grid>

        <DialogActiveInactiveEntityContainer
          heading={dailogHeading}
          dateField="End Date"
          id="ringtone-menu"
          dialogType={type}
          open={open}
          onClose={dialogClose}
        />

        <DialogOrgsUser
          heading={dailogHeading}
          pageType={pageType}
          addedOrgsId={addedOrgsId}
          roleState={userRole}
          dateField="End Date"
          id="ringtone-menu"
          dialogType={type}
          open={openUserForm}
          updateUserFormData={updateUserFormData}
          onClose={() => {
            setOpenUserForm(false);
            setUpdateUserFormData({});
          }}
        />
      </Grid>
    </>
  );
};

export default OrgsUsers;
